const REDACTED_VALUE = '[REDACTED]';
const CIRCULAR_VALUE = '[Circular]';
const OMIT_VALUE = Symbol('omit-diagnostic-value');
const DIAGNOSTIC_MIME_TYPE = 'application/json;charset=utf-8';

export const CONVERTER_JOB_PROFILE_GLB_IMPORT = 'glb-import';
export const CONVERTER_JOB_PROFILE_S123_OPTIMIZATION = 's123-optimization';

function normalizeText(value) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value.trim();

  return normalizedValue.length > 0 ? normalizedValue : undefined;
}

function normalizeDiagnosticKey(key) {
  return key.toLowerCase().replace(/[_-]/g, '');
}

function isCredentialKey(key) {
  return [
    'authorization',
    'accesstoken',
    'token',
    'cookie',
    'setcookie',
  ].includes(normalizeDiagnosticKey(key));
}

function isAbortRuntimeKey(key) {
  return [
    'abortcontroller',
    'abortsignal',
    'controller',
    'signal',
  ].includes(normalizeDiagnosticKey(key));
}

function isAbortRuntimeValue(value) {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  if (
    typeof AbortSignal !== 'undefined' &&
    value instanceof AbortSignal
  ) {
    return true;
  }

  if (
    typeof AbortController !== 'undefined' &&
    value instanceof AbortController
  ) {
    return true;
  }

  let constructorName;
  let objectTag;

  try {
    constructorName = value?.constructor?.name;
    objectTag = Object.prototype.toString.call(value);
  } catch (_error) {
    return false;
  }

  return (
    constructorName === 'AbortSignal' ||
    constructorName === 'AbortController' ||
    objectTag === '[object AbortSignal]' ||
    objectTag === '[object AbortController]'
  );
}

function sanitizeDiagnosticValue(value, ancestors) {
  if (isAbortRuntimeValue(value)) {
    return OMIT_VALUE;
  }

  if (
    value === undefined ||
    typeof value === 'function' ||
    typeof value === 'symbol'
  ) {
    return OMIT_VALUE;
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (typeof value === 'number' && !Number.isFinite(value)) {
    return null;
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }

  if (ancestors.has(value)) {
    return CIRCULAR_VALUE;
  }

  const nextAncestors = new Set(ancestors);
  nextAncestors.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => {
      const sanitizedItem = sanitizeDiagnosticValue(item, nextAncestors);

      return sanitizedItem === OMIT_VALUE ? null : sanitizedItem;
    });
  }

  const sanitizedValue = {};

  for (const key of Object.keys(value)) {
    if (isCredentialKey(key)) {
      sanitizedValue[key] = REDACTED_VALUE;
      continue;
    }

    if (isAbortRuntimeKey(key)) {
      continue;
    }

    let nestedValue;

    try {
      nestedValue = value[key];
    } catch (_error) {
      continue;
    }

    const sanitizedNestedValue = sanitizeDiagnosticValue(
      nestedValue,
      nextAncestors,
    );

    if (sanitizedNestedValue !== OMIT_VALUE) {
      sanitizedValue[key] = sanitizedNestedValue;
    }
  }

  return sanitizedValue;
}

export function redactConverterJobDiagnostics(value) {
  const sanitizedValue = sanitizeDiagnosticValue(value, new Set());

  return sanitizedValue === OMIT_VALUE ? undefined : sanitizedValue;
}

function stableSerialize(value) {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`;
  }

  if (value !== null && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
      .join(',')}}`;
  }

  return JSON.stringify(value);
}

function isJobStatusEvent(event) {
  const normalizedEvent = normalizeText(event)
    ?.toLowerCase()
    .replace(/[\s-]+/g, '_');

  return normalizedEvent === 'job_status' || normalizedEvent === 'job_status_snapshot';
}

export function buildConverterJobTechnicalEntry({
  capturedAt = new Date().toISOString(),
  event,
  payload,
} = {}) {
  return {
    capturedAt,
    event: normalizeText(event) ?? 'unknown',
    payload: redactConverterJobDiagnostics(payload) ?? null,
  };
}

export function appendConverterJobTechnicalEntry(
  entries,
  entryInput,
  { deduplicateStatusSnapshots = true } = {},
) {
  const currentEntries = Array.isArray(entries) ? entries : [];
  const nextEntry = buildConverterJobTechnicalEntry(entryInput);
  const previousEntry = currentEntries.at(-1);

  if (
    deduplicateStatusSnapshots &&
    isJobStatusEvent(nextEntry.event) &&
    previousEntry?.event === nextEntry.event &&
    stableSerialize(redactConverterJobDiagnostics(previousEntry.payload)) ===
      stableSerialize(nextEntry.payload)
  ) {
    return currentEntries;
  }

  return [...currentEntries, nextEntry];
}

export function deduplicateConsecutiveConverterJobStatuses(entries) {
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries.reduce((deduplicatedEntries, entry) => {
    const sanitizedEntry = redactConverterJobDiagnostics(entry);

    if (sanitizedEntry === undefined) {
      return deduplicatedEntries;
    }

    if (!isJobStatusEvent(entry?.event)) {
      return [...deduplicatedEntries, sanitizedEntry];
    }

    const previousEntry = deduplicatedEntries.at(-1);

    if (
      previousEntry?.event === sanitizedEntry?.event &&
      stableSerialize(previousEntry?.payload) === stableSerialize(sanitizedEntry?.payload)
    ) {
      return deduplicatedEntries;
    }

    return [...deduplicatedEntries, sanitizedEntry];
  }, []);
}

export function normalizeConverterJobError(error) {
  if (error === null || error === undefined) {
    return null;
  }

  if (typeof error !== 'object') {
    return {
      message: String(error),
    };
  }

  const response = error.response;
  const normalizedError = {
    name: normalizeText(error.name),
    message: normalizeText(error.message),
    code: normalizeText(error.code),
    messageKey: normalizeText(error.messageKey),
    details: error.details,
    responseStatusCode:
      response?.status ?? error.responseStatusCode ?? error.status,
    responseStatusText:
      normalizeText(response?.statusText) ??
      normalizeText(error.responseStatusText) ??
      normalizeText(error.statusText),
    responseData: response?.data ?? error.responseData,
  };

  return redactConverterJobDiagnostics(
    Object.fromEntries(
      Object.entries(normalizedError).filter(([, value]) => value !== undefined),
    ),
  );
}

export function buildConverterJobDebugReportObject({
  clientError,
  entries,
  error,
  generatedAt = new Date().toISOString(),
  jobId,
  jobStatus,
  latestStatus,
  profile,
  route,
  settings,
  source,
  sourceContext,
  submittedSettings,
  technicalEntries = [],
} = {}) {
  const resolvedTechnicalEntries = entries ?? technicalEntries;

  return redactConverterJobDiagnostics({
    generatedAt,
    route: route ?? null,
    profile: profile ?? null,
    sourceContext: sourceContext ?? source ?? null,
    submittedSettings: submittedSettings ?? settings ?? null,
    jobId: jobId ?? null,
    technicalEntries: deduplicateConsecutiveConverterJobStatuses(
      resolvedTechnicalEntries,
    ),
    latestStatus: latestStatus ?? jobStatus ?? null,
    error: normalizeConverterJobError(error ?? clientError),
  });
}

export function buildConverterJobDebugReport(options) {
  return JSON.stringify(buildConverterJobDebugReportObject(options), null, 2);
}

function sanitizeFileNamePart(value, fallbackValue) {
  const normalizedValue = normalizeText(value);

  if (normalizedValue === undefined) {
    return fallbackValue;
  }

  const sanitizedValue = normalizedValue
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 72)
    .replace(/-+$/g, '');

  return sanitizedValue || fallbackValue;
}

function resolveReportProfile(profile) {
  const normalizedProfile = normalizeText(profile)?.toLowerCase();

  if (
    normalizedProfile === CONVERTER_JOB_PROFILE_S123_OPTIMIZATION ||
    normalizedProfile === 's123' ||
    normalizedProfile === 'variant-optimization'
  ) {
    return CONVERTER_JOB_PROFILE_S123_OPTIMIZATION;
  }

  return CONVERTER_JOB_PROFILE_GLB_IMPORT;
}

function sanitizeTimestamp(value) {
  const date = value instanceof Date ? value : new Date(value);
  const isoTimestamp = Number.isNaN(date.getTime())
    ? new Date(0).toISOString()
    : date.toISOString();

  return isoTimestamp.replace(/[:.]/g, '-');
}

export function createConverterJobDebugFileName({
  createdAt = new Date(),
  jobId,
  modelName,
  profile = CONVERTER_JOB_PROFILE_GLB_IMPORT,
  route,
  sourceCalculationGuid,
  sourceName,
} = {}) {
  const resolvedProfile = resolveReportProfile(route ?? profile);
  const safeSourceName = sanitizeFileNamePart(
    sourceName ?? modelName ?? sourceCalculationGuid,
    'source',
  );
  const safeJobId = sanitizeFileNamePart(jobId, 'pending-job');
  const timestamp = sanitizeTimestamp(createdAt);

  return `${resolvedProfile}-debug-${safeSourceName}-${safeJobId}-${timestamp}.json`;
}

export function createConverterJobDebugDownload({
  fileNameOptions,
  report,
} = {}) {
  let reportValue = report ?? {};

  if (typeof reportValue === 'string') {
    try {
      reportValue = JSON.parse(reportValue);
    } catch (_error) {
      // A caller can still download a non-JSON diagnostic message verbatim.
    }
  }

  const text = typeof reportValue === 'string'
    ? reportValue
    : JSON.stringify(redactConverterJobDiagnostics(reportValue), null, 2);

  return {
    filename: createConverterJobDebugFileName(fileNameOptions),
    mimeType: DIAGNOSTIC_MIME_TYPE,
    text,
  };
}

export function downloadConverterJobDebugReport({
  documentRef = typeof document === 'undefined' ? undefined : document,
  fileNameOptions,
  report,
  urlApi = typeof URL === 'undefined' ? undefined : URL,
} = {}) {
  if (!documentRef || !urlApi?.createObjectURL || !urlApi?.revokeObjectURL) {
    throw new Error('Browser download APIs are unavailable.');
  }

  const download = createConverterJobDebugDownload({
    fileNameOptions,
    report,
  });
  const objectUrl = urlApi.createObjectURL(
    new Blob([download.text], { type: download.mimeType }),
  );
  const link = documentRef.createElement('a');

  link.download = download.filename;
  link.href = objectUrl;
  documentRef.body.appendChild(link);
  link.click();
  link.remove();
  urlApi.revokeObjectURL(objectUrl);
}
